import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, ChevronDown, Loader2, MapPin, Phone, Star, X } from "lucide-react";
import { Brand } from "@/components/Brand";
import { getTrialOffer, startTrialCheckout } from "@/lib/api";
import { emailWarning } from "@/lib/emailCheck";

// Public 1 Week Trial page (no login, backend routes/trial_week.py): the ad link. A foundation week that's
// worth it even if they never come back (Anish, 28 Oct): it talks about this one week only, no prices for
// anything after it, and staying on is their choice. Leads with the DIY nutrition course and meal plan;
// the sessions and the 1-1 coach are the extra.
// Then proof (real LIFTR transformations and reviews, from www.liftr.fit), where the gym is, what the
// week includes, how to win it back, and the form: name, phone, email, which Monday, then the gym's
// payment page. After paying: the bill by email, a WhatsApp from the gym, and a call from their coach.

const SITE = "https://www.liftr.fit";
// Real LIFTR members (website/src/data/transformations.ts, strongest first). t-22 in place of w-05,
// a back view without the LIFTR card (Anish, 28 Oct).
const SHOTS = ["t-26", "p-36", "p-12", "p-19", "t-22", "t-07", "t-12", "t-15"];
// Real Google reviews, as on www.liftr.fit (website/src/components/Reviews.astro).
const REVIEWS = [
  { res: "−6 kg in 6 weeks", who: "Vishnu Ammanath", text: "I lost around 6 kgs and noticed a good improvement in both my physical appearance and mental well-being. Big thanks to my coach Arjun for his constant support." },
  { res: "−4 kg in 6 weeks", who: "Aniruddha Kale", text: "Once you join, a dedicated trainer is assigned to you who monitors your diet photos and checks in regularly. My wife and I are both very happy with the experience." },
  { res: "Workouts and diet", who: "Harshita Singh", text: "They constantly motivate us, correct our posture, and make sure we're doing every exercise properly. They also guide us on diet and track our progress." },
];
// The questions people ask most before they book (Anish, 28 Oct). This week only: no prices for what comes after.
// `inPerson`: until the iOS app is approved (2 Nov) the nutrition course is done online (app.liftr.info/m) before
// their Monday, and coaching is on Trainerize, which members only ever hear called "the LIFTR app" (Anish, 3 Nov).
const faq = (inPerson) => [
  { q: "What kind of training is this?", a: [
    "Strength and conditioning. Most days you lift weights, upper body one day and lower body the next, with a coach showing you how. "
    + "Two days a week are faster, sweatier conditioning sessions.",
    "Every workout is scaled to you, so it works whether you've never been in a gym or you've trained for years."] },
  { q: "Is it group, semi-private or one-to-one training?", a: [
    "Your six sessions are coached group classes, at the class times you pick. The coach scales every exercise to you and watches your form.",
    inPerson
      ? "On top of that, your own coach works with you one-to-one on the LIFTR app all week: they call you when you sign up, check your meals and answer your questions."
      : "On top of that, you get your own accountability coach who works with you one-to-one in the app all week: they call you when you sign up, check your meals and answer your questions."] },
  { q: "Do I need to be fit already?", a: [
    "No. Most people start their first week unsure of the exercises. Tell your coach on the first call and they'll plan your week around where you are."] },
  { q: "What happens after the week?", a: [
    "Nothing, unless you want it to. There's no contract and nothing is charged after this one payment.",
    (inPerson ? "You keep everything you learned in the nutrition course" : "You keep the nutrition course, your meal plan and your grocery list")
    + ". At the end of the week your coach sits down with you to work out what you want next, "
    + "whether that's group classes, small group training, one-to-one coaching or carrying on by yourself."] },
];

const LESSONS = ["The basics: calories and your goal", "Protein, and protein per calorie", "Carbs", "Fats", "Vegetables and fibre",
  "Water", "Putting your day together", "Eating out, at home and abroad", "Habits, sleep and supplements", "Nutrition myths"];

const input = "w-full rounded-xl bg-[#111] border border-[#2a2a2a] px-4 py-3 text-base text-white placeholder:text-zinc-600 focus:border-rose-500 focus:outline-none";
const money = (sym, n) => {
  const s = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n || 0);
  return /^[A-Za-z]/.test(sym) ? `${sym} ${s}` : `${sym}${s}`;
};

const Sec = ({ label, title, children }) => (
  <section className="mt-12">
    {label && <div className="text-xs uppercase tracking-[0.2em] text-rose-400">{label}</div>}
    {title && <h2 className="mt-2 font-display text-2xl font-extrabold uppercase leading-tight">{title}</h2>}
    <div className="mt-4">{children}</div>
  </section>
);
const Tick = ({ children }) => (
  <li className="flex items-start gap-2 text-zinc-200"><Check className="mt-1 h-4 w-4 shrink-0 text-rose-400" /><span>{children}</span></li>
);

// Country codes for the phone field: the gym's own first (from the API), then where our members
// usually come from. Anyone else types their full number starting with +.
const DIAL_CODES = [
  ["+971", "UAE"], ["+91", "India"], ["+94", "Sri Lanka"], ["+966", "Saudi Arabia"], ["+974", "Qatar"],
  ["+965", "Kuwait"], ["+968", "Oman"], ["+973", "Bahrain"], ["+92", "Pakistan"], ["+880", "Bangladesh"],
  ["+977", "Nepal"], ["+63", "Philippines"], ["+20", "Egypt"], ["+44", "UK"], ["+1", "US / Canada"],
  ["+61", "Australia"], ["+65", "Singapore"], ["+60", "Malaysia"], ["+27", "South Africa"], ["+49", "Germany"],
  ["+33", "France"], ["+7", "Russia"], ["+86", "China"],
];

export default function TrialSignup() {
  const { gym } = useParams();
  const [offer, setOffer] = useState(null);
  const [missing, setMissing] = useState("");
  const [f, setF] = useState({ name: "", phone: "", email: "", start_date: "", company: "" });
  const [dial, setDial] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { document.title = "LIFTR | Foundation week"; }, []);
  useEffect(() => {
    getTrialOffer(gym)
      .then((o) => { setOffer(o); setDial(o.dial_code); setF((x) => ({ ...x, start_date: o.start_dates[0]?.date || "" })); })
      .catch((e) => setMissing(e?.response?.data?.detail || "This page isn't available right now."));
  }, [gym]);

  const pay = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const phone = f.phone.trim().startsWith("+") ? f.phone.trim() : `${dial} ${f.phone.trim()}`;
      const r = await startTrialCheckout(gym, { ...f, phone });
      window.location.href = r.checkout_url;
    } catch (ex) {
      setError(ex?.response?.data?.detail || "Couldn't open the payment page. Try again in a minute.");
      setBusy(false);
    }
  };

  if (missing) return <Shell><p className="mt-10 text-lg text-zinc-300">{missing}</p></Shell>;
  if (!offer) return <Shell><Loader2 className="mt-10 h-6 w-6 animate-spin text-zinc-500" /></Shell>;
  const sym = offer.currency_symbol;
  const price = money(sym, offer.price);
  const loc = offer.location || {};
  const warn = f.email ? emailWarning(f.email) : null;
  const toForm = () => document.getElementById("join")?.scrollIntoView({ behavior: "smooth" });
  const stack = offer.value_stack || [];
  const inPerson = !!offer.in_person;
  const worth = stack.reduce((n, r) => n + r.value, 0);
  return (
    <Shell bar={(
      <button type="button" onClick={toForm} data-testid="trial-sticky"
        className="h-10 rounded-full bg-rose-600 px-5 text-sm font-semibold text-white">
        Pick my Monday · {money(sym, offer.total)}
      </button>
    )}>
      <div className="mt-6 text-xs uppercase tracking-[0.2em] text-rose-400">LIFTR {loc.area || offer.gym}</div>
      <h1 className="mt-2 font-display text-4xl font-extrabold uppercase leading-none">One week.<br />Win it back.</h1>
      <p className="mt-4 text-lg text-zinc-300">
        {inPerson
          ? `${price} gets you our nutrition course, six coached sessions and a coach in your corner for a week.`
          : `${price} gets you our nutrition course, your own meal plan, six coached sessions and a coach in your corner for a week.`}
        Do the work and we give you the {price} back.
      </p>
      <button type="button" onClick={toForm} className="mt-6 h-12 w-full rounded-full bg-rose-600 text-base font-semibold text-white">
        Pick my Monday
      </button>
      <a href="https://www.google.com/maps/search/LIFTR+Strength+%26+Conditioning" target="_blank" rel="noreferrer" className="mt-4 flex items-center justify-center gap-2 text-sm text-zinc-400">
        <span className="flex text-amber-400">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-4 w-4 fill-current" />)}</span>
        4.5 from 1,000+ Google reviews
      </a>

      {stack.length > 0 && (
        <section className="mt-8 rounded-2xl border border-rose-500/40 bg-[#111] p-5" data-testid="trial-tldr">
          <div className="text-xs uppercase tracking-[0.2em] text-rose-400">In short</div>
          <h2 className="mt-1 font-display text-xl font-extrabold uppercase">Your week includes</h2>
          <ul className="mt-4 space-y-2.5">
            {stack.map((r) => (
              <li key={r.label} className="flex items-baseline justify-between gap-3 text-zinc-200">
                <span className="flex items-start gap-2"><Check className="mt-1 h-4 w-4 shrink-0 text-rose-400" />{r.label}</span>
                <span className="shrink-0 text-sm text-zinc-400">worth {money(sym, r.value)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex items-baseline justify-between border-t border-[#2a2a2a] pt-4">
            <span className="text-zinc-400">Total value</span>
            <span className="text-lg text-zinc-500 line-through">{money(sym, worth)}</span>
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <span className="font-semibold text-white">Your foundation week</span>
            <span className="font-display text-3xl font-extrabold text-rose-400">{money(sym, offer.total)}</span>
          </div>
          <p className="mt-2 text-sm text-zinc-400">No contract, nothing after it unless you want it.</p>
        </section>
      )}

      <Sec label="Start with food" title="The part that changes everything">
        {inPerson ? (
          <>
            <p className="text-zinc-300">
              Most people don&apos;t need a new workout. They need to know what to eat. Our nutrition course is the same one our members
              do: short lessons, about 35 minutes in all, on your phone, then a 7-question test. You do it before your Monday, so you walk
              in on day one already knowing how to eat for your goal. It clears up the myths most of us grew up with, and it&apos;s
              knowledge you keep for life.
            </p>
            <ul className="mt-4 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">{LESSONS.map((l) => <Tick key={l}>{l}</Tick>)}</ul>
          </>
        ) : (<>
        <p className="text-zinc-300">
          Most people don&apos;t need a new workout. They need to know what to eat. Our DIY nutrition course is the same one our
          members do: short lessons, about 35 minutes in all, in the LIFTR app, each with a quick quiz. It clears up the myths most of us
          grew up with, and it&apos;s knowledge you keep for life. As you go, you pick your top proteins, carbs and fats.
        </p>
        <ul className="mt-4 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">{LESSONS.map((l) => <Tick key={l}>{l}</Tick>)}</ul>
        <p className="mt-4 text-zinc-300">
          Then you build your own meal plan from the foods you actually like, with your calories and protein worked out for you, and a
          grocery list. You keep it after the week. That alone is worth the {price}.
        </p>
        </>)}
      </Sec>

      <Sec label="Why food first" title="Same gym. Different week.">
        <p className="mb-4 text-zinc-300">What our coaches see again and again, in someone&apos;s first week or two:</p>
        <div className="grid grid-cols-2 gap-2 text-sm" data-testid="trial-compare">
          <div className="rounded-xl border border-[#2a2a2a] bg-[#111] p-3">
            <div className="mb-2 font-semibold text-zinc-400">Gym first, food later</div>
            <ul className="space-y-2 text-zinc-400">
              {["Tired halfway through sessions", "Sore for days, slow to recover", "Poor sleep", "Hungry and snacking at night",
                "No change on the scale", "Quits in a week to ten days"].map((x) => (
                <li key={x} className="flex items-start gap-1.5"><X className="mt-0.5 h-4 w-4 shrink-0 text-zinc-600" />{x}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-rose-500/40 bg-[#111] p-3">
            <div className="mb-2 font-semibold text-white">Food first, then the gym</div>
            <ul className="space-y-2 text-zinc-200">
              {["More energy in every session", "Recovers faster", "Sleeps better", "Focused, fewer cravings",
                "Sees real change", "Keeps showing up"].map((x) => (
                <li key={x} className="flex items-start gap-1.5"><Check className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" />{x}</li>
              ))}
            </ul>
          </div>
        </div>
        <p className="mt-3 text-zinc-300">That&apos;s why your week starts with the nutrition course, not the barbell.</p>
      </Sec>

      <Sec label="Then the icing" title="A coach and six sessions">
        <ul className="space-y-3">
          <Tick><b className="text-white">Six coached sessions</b>, Monday to Saturday{loc.times ? `: ${loc.times}` : loc.batches ? `, at ${loc.batches.join(", ")}` : ""}. Beginners welcome: the coach scales every workout to you.</Tick>
          {inPerson ? (
            <Tick><b className="text-white">Your own coach</b>, who calls you the day you sign up and coaches you 1-1 on the LIFTR app all week: meal photos, check-ins and questions.</Tick>
          ) : (<>
            <Tick><b className="text-white">Your own accountability coach</b>, who calls you the day you sign up and checks in on you 1-1 in the app all week.</Tick>
            <Tick><b className="text-white">Coach AInish</b>, our AI coach, to log your meals and answer questions any time.</Tick>
          </>)}
        </ul>
      </Sec>

      <Sec label="No strings" title="You don't have to stay">
        <p className="text-zinc-300">
          This week stands on its own. Come for six sessions, learn how to eat, leave with your own meal plan, and use it as the first
          step of your own fitness journey, wherever it takes you.
        </p>
        <p className="mt-3 text-zinc-300">
          If you love it (we hope you do), we run large group classes, small group training and 1:1 coaching. At the end of your week we
          sit down with you and work out what you want next. If that&apos;s nothing, that&apos;s fine too.
        </p>
      </Sec>

      <Sec label="Real members" title="What our members have done">
        <div className="grid grid-cols-2 gap-2">
          {SHOTS.map((s) => (
            <img key={s} src={`${SITE}/img/ba/${s}.jpg`} alt="LIFTR member before and after" loading="lazy"
              className="aspect-square w-full rounded-lg object-cover" />
          ))}
        </div>
        <p className="mt-2 text-xs text-zinc-500">Real LIFTR members. <a className="underline" href={`${SITE}/transformations`} target="_blank" rel="noreferrer">See more</a></p>
      </Sec>

      <Sec label="Reviews" title="What members say">
        <div className="space-y-3">
          {REVIEWS.map((r) => (
            <figure key={r.who} className="rounded-xl border border-[#2a2a2a] bg-[#111] p-4">
              <div className="text-sm font-semibold text-rose-300">{r.res}</div>
              <span className="flex text-amber-400">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-3.5 w-3.5 fill-current" />)}</span>
              <blockquote className="mt-2 text-zinc-200">&ldquo;{r.text}&rdquo;</blockquote>
              <figcaption className="mt-2 text-xs text-zinc-500">{r.who} · Google review</figcaption>
            </figure>
          ))}
        </div>
      </Sec>

      {loc.lat && (
        <Sec label="Where we are" title={loc.area}>
          {loc.travel_note && <p className="mb-3 text-zinc-300">{loc.travel_note}</p>}
          <iframe title="LIFTR on Google Maps" src={`https://www.google.com/maps?q=${loc.lat},${loc.lng}&z=14&output=embed`}
            className="h-64 w-full rounded-xl border border-[#2a2a2a]" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          <div className="mt-3 flex flex-wrap gap-4 text-sm">
            <a href={loc.maps} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-rose-300 underline"><MapPin className="h-4 w-4" />Open in Google Maps</a>
            {loc.phone && <a href={`tel:${loc.phone}`} className="flex items-center gap-1 text-zinc-300"><Phone className="h-4 w-4" />{loc.phone}</a>}
          </div>
        </Sec>
      )}

      <Sec label="Your week" title="What you get">
        <ul className="space-y-2">{offer.includes.map((x) => <Tick key={x}>{x}</Tick>)}</ul>
      </Sec>

      <Sec label="Win it back" title={`Get your ${price} back`}>
        <ol className="list-decimal space-y-1 pl-5 text-zinc-200">
          <li>Train {offer.visits_needed} times in your week</li>
          {inPerson ? (<>
            <li>Pass the nutrition test, before your Monday</li>
            <li>Reply to your coach on the LIFTR app</li>
          </>) : (<>
            <li>Finish the nutrition course in the app</li>
            <li>Set your meal plan</li>
            <li>Reply to your coach in the app</li>
          </>)}
        </ol>
        <p className="mt-3 text-zinc-300">
          Do all {inPerson ? "three" : "four"} and we give you the {price} back as credit, for whatever you choose to do with us next. Use it within {offer.credit_days} days
          after your week, or don&apos;t: the week is yours either way.
        </p>
      </Sec>

      <Sec label="Questions" title="Good to know">
        <div className="space-y-2" data-testid="trial-faq">
          {faq(inPerson).map((x) => (
            <details key={x.q} className="group rounded-xl border border-[#2a2a2a] bg-[#111] p-4">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 font-semibold text-white">
                {x.q}<ChevronDown className="h-4 w-4 shrink-0 text-zinc-500 transition-transform group-open:rotate-180" />
              </summary>
              <div className="mt-3 space-y-2 text-zinc-300">{x.a.map((t) => <p key={t}>{t}</p>)}</div>
            </details>
          ))}
        </div>
      </Sec>

      <section id="join" className="mt-12 scroll-mt-6">
        <h2 className="font-display text-2xl font-extrabold uppercase">Pick your Monday</h2>
        <form onSubmit={pay} className="mt-4 space-y-4" data-testid="trial-form">
          <input className={input} placeholder="Full name" autoComplete="name" value={f.name} required
            onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="trial-name" />
          <div className="flex gap-2">
            {/* The box shows just the code; the list underneath has the country names. */}
            <label className={`relative flex w-[5.5rem] shrink-0 items-center justify-between rounded-xl border border-[#2a2a2a] bg-[#111] px-3 text-zinc-200 ${f.phone.trim().startsWith("+") ? "opacity-40" : ""}`}>
              {dial}<ChevronDown className="h-4 w-4 text-zinc-500" />
              <select value={dial} onChange={(e) => setDial(e.target.value)} aria-label="Country code" data-testid="trial-dial"
                disabled={f.phone.trim().startsWith("+")} className="absolute inset-0 cursor-pointer opacity-0">
                {[DIAL_CODES.find(([c]) => c === offer.dial_code) || [offer.dial_code, ""], ...DIAL_CODES.filter(([c]) => c !== offer.dial_code)].map(([c, n]) => (
                  <option key={c} value={c}>{c}{n ? ` ${n}` : ""}</option>
                ))}
              </select>
            </label>
            <input className={input} placeholder="Mobile number" type="tel" autoComplete="tel" value={f.phone} required
              onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="trial-phone" />
          </div>
          <div>
            <input className={input} placeholder={inPerson ? "Email (your receipt goes here)" : "Email (your receipt and app login codes go here)"} type="email" autoComplete="email" value={f.email} required
              onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="trial-email" />
            {warn && <p className="mt-1 text-sm text-amber-300">{warn}</p>}
          </div>
          <fieldset>
            <legend className="mb-2 text-sm text-zinc-400">Every foundation week starts on a Monday. Pick yours:</legend>
            <div className="flex flex-wrap gap-2">
              {offer.start_dates.map((d) => (
                <button type="button" key={d.date} onClick={() => setF({ ...f, start_date: d.date })} aria-pressed={f.start_date === d.date}
                  className={`rounded-full border px-4 py-2 text-sm ${f.start_date === d.date ? "border-rose-500 bg-rose-500/15 text-white" : "border-[#2a2a2a] text-zinc-300"}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </fieldset>
          <input tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={f.company}
            onChange={(e) => setF({ ...f, company: e.target.value })} />
          {error && <p className="text-sm text-amber-300" role="alert">{error}</p>}
          <button type="submit" disabled={busy} data-testid="trial-pay"
            className="h-14 w-full rounded-full bg-rose-600 text-lg font-semibold text-white disabled:opacity-60">
            {busy ? "Opening payment…" : `Pay ${money(sym, offer.total)}`}
          </button>
          <p className="text-center text-xs text-zinc-500">
            {offer.taxed ? `${price} + ${offer.tax_name || "tax"}. ` : ""}One foundation week per person.
          </p>
        </form>
        <div className="mt-6 rounded-xl border border-[#2a2a2a] bg-[#111] p-4 text-sm text-zinc-300">
          <div className="mb-2 font-semibold text-white">After you pay</div>
          <ul className="space-y-1.5">
            <Tick>Your receipt comes by email</Tick>
            <Tick>We message you on WhatsApp</Tick>
            <Tick>Your coach calls you to say hi and plan your week</Tick>
            {inPerson
              ? <Tick>You start the nutrition course online, and we set you up on the LIFTR app</Tick>
              : <Tick>You get the LIFTR app and start the nutrition course</Tick>}
          </ul>
        </div>
      </section>
    </Shell>
  );
}

function Shell({ children, bar = null }) {
  return (
    <div className="min-h-screen bg-[#0a0a0a] px-4 pb-16 text-white">
      <div className="mx-auto max-w-md">
        {/* Stays at the top while scrolling, with the button to the form (Anish, 28 Oct) */}
        <div className="sticky top-0 z-20 -mx-4 flex items-center justify-between border-b border-[#1a1a1a] bg-[#0a0a0a] px-4 py-3">
          <Brand showText={false} />
          {bar}
        </div>
        {children}
      </div>
    </div>
  );
}
